import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  PropsWithChildren,
  startTransition,
} from 'react';
import { useTranslation, getPrimaryLanguage } from '@/services/translation';
import { storage, StorageKey } from '@/services/storage';
import { Language, DEFAULT_LANGUAGE } from '@/entities';

interface ILanguageContextValue {
  language: Language,
  onLanguageChange: (language: Language) => void,
}

const initialValue = {
  language: DEFAULT_LANGUAGE,
  onLanguageChange: () => {},
};

const LanguageContext = createContext<ILanguageContextValue>(initialValue);

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageContextProvider');
  }
  return context;
};

const isLanguage = (value: any): value is Language => {
  return value in Language;
};

const getAutoDetectedLanguage = () => {
  const language = typeof window?.navigator?.language === 'string' ? window.navigator.language.toLowerCase().split('-')[0] : undefined;
  return isLanguage(language) ? language : DEFAULT_LANGUAGE;
};

export const LanguageContextProvider = ({ children }: PropsWithChildren) => {
  const { i18n } = useTranslation();

  const [language, setLanguage] = useState(
    () => {
      return getPrimaryLanguage(storage.getItem<Language>(StorageKey.LANGUAGE) || getAutoDetectedLanguage()) || DEFAULT_LANGUAGE;
    },
  );

  const handleLanguageChange = useCallback((nextLanguage: Language) => {
    startTransition(() => {
      setLanguage(nextLanguage);
    });
  }, [setLanguage]);

  useEffect(() => {
    storage.setItem(StorageKey.LANGUAGE, language);
    i18n.changeLanguage(language);
  }, [language]);

  const value = useMemo(
    () => {
      return {
        language,
        onLanguageChange: handleLanguageChange,
      };
    },
    [language, setLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
